/**
 * Breathing pacer — the web twin of the app's BreathingScreen
 * (android/app-ui/…/BreathingScreen.kt): same four patterns, same
 * raised-cosine ease, same phase labels, so practice feels identical on
 * both platforms. Pure UI over whatever the studio is playing — slow
 * breathing pairs naturally with a theta ramp.
 *
 * Voice guidance uses the browser's speechSynthesis: the web equivalent of
 * the app's on-device TTS principle (free, local, no server). Unlike
 * Android there is no pre-synthesis path — SpeechSynthesis cannot render
 * to a buffer — so cues are spoken live with cancel-before-speak; browser
 * TTS latency on two-word utterances is small enough to track the orb.
 * Keep the cue strings identical to the Android Phase labels.
 */

// ------------------------------------------------------------ pure logic

export interface BreathPattern {
  name: string;
  inhale: number;
  holdIn: number;
  exhale: number;
  holdOut: number;
}

export const BREATH_PATTERNS: BreathPattern[] = [
  { name: "Calm", inhale: 4, holdIn: 0, exhale: 6, holdOut: 0 },
  { name: "Box", inhale: 4, holdIn: 4, exhale: 4, holdOut: 4 },
  { name: "4-7-8", inhale: 4, holdIn: 7, exhale: 8, holdOut: 0 },
  { name: "Resonant", inhale: 5.5, holdIn: 0, exhale: 5.5, holdOut: 0 },
];

export function patternTotal(p: BreathPattern): number {
  return p.inhale + p.holdIn + p.exhale + p.holdOut;
}

/** Pinned to the Android Phase enum labels — one voice across platforms. */
export const BREATH_CUE_LABELS = {
  inhale: "Breathe in",
  holdIn: "Hold",
  exhale: "Breathe out",
  holdOut: "Rest",
} as const;

export type BreathPhase = keyof typeof BREATH_CUE_LABELS;

export interface CycleState {
  phase: BreathPhase;
  label: string;
  secondsLeft: number;
  /** Orb size 0..1 (raised-cosine eased). */
  size: number;
}

/** Where in the cycle time t (seconds, 0 ≤ t < total) falls. */
export function cycleState(pattern: BreathPattern, t: number): CycleState {
  const ease = (f: number): number => 0.5 * (1 - Math.cos(Math.PI * f));
  let x = t;
  if (x < pattern.inhale) {
    return {
      phase: "inhale",
      label: BREATH_CUE_LABELS.inhale,
      secondsLeft: pattern.inhale - x,
      size: ease(x / pattern.inhale),
    };
  }
  x -= pattern.inhale;
  if (x < pattern.holdIn) {
    return { phase: "holdIn", label: BREATH_CUE_LABELS.holdIn, secondsLeft: pattern.holdIn - x, size: 1 };
  }
  x -= pattern.holdIn;
  if (x < pattern.exhale) {
    return {
      phase: "exhale",
      label: BREATH_CUE_LABELS.exhale,
      secondsLeft: pattern.exhale - x,
      size: 1 - ease(x / pattern.exhale),
    };
  }
  x -= pattern.exhale;
  return { phase: "holdOut", label: BREATH_CUE_LABELS.holdOut, secondsLeft: pattern.holdOut - x, size: 0 };
}

// ------------------------------------------------------------------ voice

const VOICE_PREF_KEY = "kalmtide.breathVoice";

function speakCue(label: string): void {
  if (!("speechSynthesis" in window)) return;
  // The web QUEUE_FLUSH: a slow engine must never stack stale cues.
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(label);
  u.rate = 0.9;
  window.speechSynthesis.speak(u);
}

// ---------------------------------------------------------------- overlay

const VOICE_ON_ICON = `<svg viewBox="0 0 16 16" fill="currentColor"><path d="M2 6v4h2.8L9 13.4V2.6L4.8 6H2z"/><path d="M10.8 5.2a3.6 3.6 0 0 1 0 5.6V9.4a2.2 2.2 0 0 0 0-2.8V5.2z" opacity="0.8"/><path d="M10.8 1.9v1.5a5 5 0 0 1 0 9.2v1.5a6.4 6.4 0 0 0 0-12.2z" opacity="0.6"/></svg>`;
const VOICE_OFF_ICON = `<svg viewBox="0 0 16 16" fill="currentColor"><path d="M2 6v4h2.8L9 13.4V2.6L4.8 6H2z"/><path d="M10.6 5.6 15 10m0-4.4L10.6 10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" fill="none"/></svg>`;
const PLAY_GLYPH = `<svg viewBox="0 0 16 16" fill="currentColor"><path d="M4.5 2.5v11L13.5 8z"/></svg>`;
const PAUSE_GLYPH = `<svg viewBox="0 0 16 16" fill="currentColor"><rect x="3.5" y="2.5" width="3" height="11" rx="1"/><rect x="9.5" y="2.5" width="3" height="11" rx="1"/></svg>`;
const RESTART_GLYPH = `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9"/><path d="M13.7 1.8v2.7h-2.7" stroke-linejoin="round"/></svg>`;

/** Full-screen pacer overlay; returns after mounting (self-closing). */
export function openBreathingOverlay(): void {
  const overlay = document.createElement("div");
  overlay.className = "breath-overlay";
  overlay.innerHTML = `
    <div class="breath-head">
      <button class="icon-btn" data-role="close" title="Close">✕</button>
      <span class="breath-title">BREATHE</span>
      <button class="icon-btn breath-voice" data-role="voice"
        title="Voice guidance — spoken cues for eyes-closed practice"></button>
    </div>
    <div class="breath-chips">
      ${BREATH_PATTERNS.map(
        (p, i) => `<button class="solf-chip${i === 0 ? " selected" : ""}" data-pattern="${i}">${p.name}</button>`,
      ).join("")}
    </div>
    <div class="breath-orb">
      <svg viewBox="0 0 200 200">
        <defs>
          <radialGradient id="breath-fill">
            <stop offset="0%" stop-color="var(--teal)" stop-opacity="0.95"/>
            <stop offset="100%" stop-color="var(--teal)" stop-opacity="0.55"/>
          </radialGradient>
        </defs>
        <circle cx="100" cy="100" r="91" fill="var(--teal)" opacity="0.10"/>
        <circle data-role="ring2" cx="100" cy="100" fill="none" stroke="var(--teal)" stroke-opacity="0.35" stroke-width="2"/>
        <circle data-role="ring1" cx="100" cy="100" fill="none" stroke="var(--teal)" stroke-opacity="0.6" stroke-width="2.6"/>
        <circle data-role="core" cx="100" cy="100" fill="url(#breath-fill)"/>
      </svg>
    </div>
    <div class="breath-readout">
      <div class="breath-phase" data-role="phase"></div>
      <div class="breath-count" data-role="count"></div>
      <div class="breath-cycles" data-role="cycles"></div>
    </div>
    <div class="breath-controls">
      <button class="icon-btn" data-role="restart" title="Restart">${RESTART_GLYPH}</button>
      <button class="breath-play" data-role="toggle" title="Pause / resume">${PAUSE_GLYPH}</button>
    </div>
  `;

  const q = <T extends HTMLElement>(sel: string): T => overlay.querySelector<T>(sel)!;
  const core = overlay.querySelector<SVGCircleElement>('[data-role="core"]')!;
  const ring1 = overlay.querySelector<SVGCircleElement>('[data-role="ring1"]')!;
  const ring2 = overlay.querySelector<SVGCircleElement>('[data-role="ring2"]')!;
  const phaseEl = q('[data-role="phase"]');
  const countEl = q('[data-role="count"]');
  const cyclesEl = q('[data-role="cycles"]');
  const voiceBtn = q<HTMLButtonElement>('[data-role="voice"]');
  const toggleBtn = q<HTMLButtonElement>('[data-role="toggle"]');

  let pattern = BREATH_PATTERNS[0]!;
  let running = true;
  let t = 0;
  let cycles = 0;
  let lastPhase: BreathPhase | null = null;
  let voiceOn = localStorage.getItem(VOICE_PREF_KEY) === "1";

  const voiceSupported = "speechSynthesis" in window;
  if (!voiceSupported) voiceBtn.style.display = "none";
  const paintVoice = (): void => {
    voiceBtn.innerHTML = voiceOn ? VOICE_ON_ICON : VOICE_OFF_ICON;
    voiceBtn.classList.toggle("on", voiceOn);
  };
  paintVoice();
  voiceBtn.addEventListener("click", () => {
    voiceOn = !voiceOn;
    localStorage.setItem(VOICE_PREF_KEY, voiceOn ? "1" : "0");
    paintVoice();
    if (voiceOn) {
      // Re-orient an eyes-closed user immediately; also warms lazy engines.
      lastPhase = null;
    } else {
      window.speechSynthesis.cancel();
    }
  });

  // The pacer must not dim mid-exhale (the app's keepScreenOn). Best-effort.
  let wakeLock: { release(): Promise<void> } | null = null;
  void (async () => {
    try {
      wakeLock = (await (navigator as Navigator & {
        wakeLock?: { request(type: "screen"): Promise<{ release(): Promise<void> }> };
      }).wakeLock?.request("screen")) ?? null;
    } catch {
      // Denied or unsupported — the pacer still works, the screen may dim.
    }
  })();

  let raf = 0;
  let lastFrame: number | null = null;
  const frame = (now: number): void => {
    if (lastFrame !== null && running) {
      t += (now - lastFrame) / 1000;
      const total = patternTotal(pattern);
      if (t >= total) {
        t -= total;
        cycles++;
      }
    }
    lastFrame = now;

    const s = cycleState(pattern, t);
    const r = 84 * (0.35 + 0.65 * s.size);
    core.setAttribute("r", String(r));
    ring1.setAttribute("r", String(r * 1.12));
    ring2.setAttribute("r", String(r * 1.28));
    phaseEl.textContent = s.label;
    countEl.textContent = String(Math.floor(s.secondsLeft) + 1);
    cyclesEl.textContent = cycles === 1 ? "1 breath" : `${cycles} breaths`;

    if (running && voiceOn && s.phase !== lastPhase) speakCue(s.label);
    if (running) lastPhase = s.phase;

    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  overlay.querySelectorAll<HTMLElement>("[data-pattern]").forEach((el) =>
    el.addEventListener("click", () => {
      overlay.querySelectorAll("[data-pattern]").forEach((c) => c.classList.remove("selected"));
      el.classList.add("selected");
      pattern = BREATH_PATTERNS[Number(el.dataset.pattern)]!;
      t = 0;
      cycles = 0;
      lastPhase = null; // announce the fresh inhale
    }),
  );

  q('[data-role="restart"]').addEventListener("click", () => {
    t = 0;
    cycles = 0;
    lastPhase = null;
  });

  toggleBtn.addEventListener("click", () => {
    running = !running;
    toggleBtn.innerHTML = running ? PAUSE_GLYPH : PLAY_GLYPH;
    if (running) {
      lastPhase = null; // resuming re-speaks the current phase
    } else if (voiceSupported) {
      window.speechSynthesis.cancel();
    }
  });

  const close = (): void => {
    cancelAnimationFrame(raf);
    if (voiceSupported) window.speechSynthesis.cancel(); // no trailing cue
    void wakeLock?.release().catch(() => undefined);
    document.removeEventListener("keydown", onKey);
    overlay.remove();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey);
  q('[data-role="close"]').addEventListener("click", close);

  document.body.append(overlay);
}
